<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useFontSize } from 'dashboard/composables/useFontSize';
import { useBranding } from 'shared/composables/useBranding';
import { clearCookiesOnLogout } from 'dashboard/store/utils/api.js';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { downloadJsonFile } from 'dashboard/helper/downloadHelper';
import ApiReferenceAPI from 'dashboard/api/apiReference';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { parseBoolean } from '@chatwoot/utils';
import { SIGNATURE_POSITIONS } from 'dashboard/helper/editorHelper';
import UserProfilePicture from './UserProfilePicture.vue';
import UserBasicDetails from './UserBasicDetails.vue';
import MessageSignature from './MessageSignature.vue';
import FontSize from './FontSize.vue';
import UserLanguageSelect from './UserLanguageSelect.vue';
import ChangePassword from './ChangePassword.vue';
import NotificationPreferences from './NotificationPreferences.vue';
import AudioNotifications from './AudioNotifications.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import AccessToken from './AccessToken.vue';
import MfaSettingsCard from './MfaSettingsCard.vue';
import ActiveSessions from './ActiveSessions.vue';
import Policy from 'dashboard/components/policy.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  ROLES,
  CONVERSATION_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

export default {
  components: {
    MessageSignature,
    SectionLayout,
    FontSize,
    UserLanguageSelect,
    UserProfilePicture,
    Policy,
    UserBasicDetails,
    RadioCard,
    ChangePassword,
    NotificationPreferences,
    AudioNotifications,
    AccessToken,
    MfaSettingsCard,
    ActiveSessions,
    BaseSettingsHeader,
    TabBar,
    NextButton,
  },
  setup() {
    const { isEditorHotKeyEnabled, updateUISettings } = useUISettings();
    const { currentFontSize, updateFontSize } = useFontSize();
    const { replaceInstallationName } = useBranding();

    return {
      currentFontSize,
      updateFontSize,
      isEditorHotKeyEnabled,
      updateUISettings,
      replaceInstallationName,
    };
  },
  data() {
    return {
      avatarFile: '',
      avatarUrl: '',
      isDownloadingReference: false,
      name: '',
      displayName: '',
      email: '',
      messageSignature: '',
      messageSignaturePosition: SIGNATURE_POSITIONS.ABOVE,
      hotKeys: [
        {
          key: 'enter',
          title: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.ENTER_KEY.HEADING'
          ),
          description: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.ENTER_KEY.CONTENT'
          ),
          lightImage: '/assets/images/dashboard/profile/hot-key-enter.svg',
          darkImage: '/assets/images/dashboard/profile/hot-key-enter-dark.svg',
        },
        {
          key: 'cmd_enter',
          title: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.CMD_ENTER_KEY.HEADING'
          ),
          description: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.CMD_ENTER_KEY.CONTENT'
          ),
          lightImage: '/assets/images/dashboard/profile/hot-key-ctrl-enter.svg',
          darkImage:
            '/assets/images/dashboard/profile/hot-key-ctrl-enter-dark.svg',
        },
      ],
      notificationPermissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      audioNotificationPermissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      activeTabIndex: 0,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentUserId: 'getCurrentUserID',
      globalConfig: 'globalConfig/get',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
    }),
    tabs() {
      return [
        { key: 'general', label: this.$t('PROFILE_SETTINGS.TABS.GENERAL') },
        {
          key: 'preferences',
          label: this.$t('PROFILE_SETTINGS.TABS.PREFERENCES'),
        },
        {
          key: 'signature',
          label: this.$t('PROFILE_SETTINGS.TABS.SIGNATURE'),
        },
        { key: 'security', label: this.$t('PROFILE_SETTINGS.TABS.SECURITY') },
        {
          key: 'notifications',
          label: this.$t('PROFILE_SETTINGS.TABS.NOTIFICATIONS'),
        },
        { key: 'api', label: this.$t('PROFILE_SETTINGS.TABS.API') },
      ];
    },
    activeTabKey() {
      return this.tabs[this.activeTabIndex]?.key;
    },
    apiAndWebhooksEnabled() {
      if (!this.isOnChatwootCloud) return true;

      return this.currentUser.accounts.some(
        account => account.api_and_webhooks
      );
    },
    accessTokenDescription() {
      if (!this.apiAndWebhooksEnabled) {
        return this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.PAID_PLAN_NOTE');
      }

      return this.replaceInstallationName(
        this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.NOTE')
      );
    },
    apiReferenceDescription() {
      return this.replaceInstallationName(
        this.$t('PROFILE_SETTINGS.FORM.API_REFERENCE.NOTE')
      );
    },
    isMfaEnabled() {
      return parseBoolean(window.chatwootConfig?.isMfaEnabled);
    },
  },
  mounted() {
    if (this.currentUserId) {
      this.initializeUser();
    }
  },
  methods: {
    changeTab(tab) {
      this.activeTabIndex = this.tabs.findIndex(t => t.key === tab.key);
    },
    initializeUser() {
      this.name = this.currentUser.name;
      this.email = this.currentUser.email;
      this.avatarUrl = this.currentUser.avatar_url;
      this.displayName = this.currentUser.display_name;
      this.messageSignature = this.currentUser.message_signature;
      this.messageSignaturePosition =
        this.currentUser.message_signature_position ||
        SIGNATURE_POSITIONS.ABOVE;
    },
    async dispatchUpdate(payload, successMessage, errorMessage) {
      let alertMessage = '';
      try {
        await this.$store.dispatch('updateProfile', payload);
        alertMessage = successMessage;

        return true; // return the value so that the status can be known
      } catch (error) {
        alertMessage = parseAPIErrorResponse(error) || errorMessage;

        return false; // return the value so that the status can be known
      } finally {
        useAlert(alertMessage);
      }
    },
    async updateProfile(userAttributes) {
      const { name, email, displayName } = userAttributes;
      const hasEmailChanged = this.currentUser.email !== email;
      this.name = name || this.name;
      this.email = email || this.email;
      this.displayName = displayName || this.displayName;

      const updatePayload = {
        name: this.name,
        email: this.email,
        displayName: this.displayName,
        avatar: this.avatarFile,
      };

      const success = await this.dispatchUpdate(
        updatePayload,
        hasEmailChanged
          ? this.$t('PROFILE_SETTINGS.AFTER_EMAIL_CHANGED')
          : this.$t('PROFILE_SETTINGS.UPDATE_SUCCESS'),
        this.$t('RESET_PASSWORD.API.ERROR_MESSAGE')
      );

      if (hasEmailChanged && success) clearCookiesOnLogout();
    },
    async updateSignature(signature) {
      const payload = { message_signature: signature };
      let successMessage = this.$t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_SUCCESS'
      );
      let errorMessage = this.$t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_ERROR'
      );

      await this.dispatchUpdate(payload, successMessage, errorMessage);
    },
    async updateSignaturePosition(position) {
      this.messageSignaturePosition = position;
      const payload = { message_signature_position: position };
      const successMessage = this.$t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION.UPDATE_SUCCESS'
      );
      const errorMessage = this.$t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION.UPDATE_ERROR'
      );

      await this.dispatchUpdate(payload, successMessage, errorMessage);
    },
    updateProfilePicture({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async deleteProfilePicture() {
      try {
        await this.$store.dispatch('deleteAvatar');
        this.avatarUrl = '';
        this.avatarFile = '';
        useAlert(this.$t('PROFILE_SETTINGS.AVATAR_DELETE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('PROFILE_SETTINGS.AVATAR_DELETE_FAILED'));
      }
    },
    toggleHotKey(key) {
      this.hotKeys = this.hotKeys.map(hotKey =>
        hotKey.key === key ? { ...hotKey, active: !hotKey.active } : hotKey
      );
      this.updateUISettings({ editor_message_key: key });
      useAlert(this.$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.UPDATE_SUCCESS'));
    },
    async onCopyToken(value) {
      if (!this.apiAndWebhooksEnabled) return;

      await copyTextToClipboard(value);
      useAlert(this.$t('COMPONENTS.CODE.COPY_SUCCESSFUL'));
    },
    // The reference is generated from the API's own OpenAPI spec, so what it describes is what the
    // token next to it can do.
    async downloadPostmanCollection() {
      this.isDownloadingReference = true;
      try {
        const { data } = await ApiReferenceAPI.postmanCollection();
        downloadJsonFile(
          `${this.replaceInstallationName('Chatwoot')}.postman_collection.json`,
          await data.text()
        );
      } catch (error) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API_REFERENCE.ERROR'));
      } finally {
        this.isDownloadingReference = false;
      }
    },
    async resetAccessToken() {
      if (!this.apiAndWebhooksEnabled) return;

      const success = await this.$store.dispatch('resetAccessToken');
      if (success) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.RESET_SUCCESS'));
      } else {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.RESET_ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="grid max-w-2xl ltr:mr-auto rtl:ml-auto">
    <BaseSettingsHeader :title="$t('PROFILE_SETTINGS.TITLE')" description="">
      <template #tabs>
        <TabBar
          :tabs="tabs"
          :initial-active-tab="activeTabIndex"
          @tab-changed="changeTab"
        />
      </template>
    </BaseSettingsHeader>

    <template v-if="activeTabKey === 'general'">
      <SectionLayout title="" description="" class="!pt-6">
        <div class="flex flex-col gap-6">
          <UserProfilePicture
            :src="avatarUrl"
            :name="name"
            @change="updateProfilePicture"
            @delete="deleteProfilePicture"
          />
          <UserBasicDetails
            :name="name"
            :display-name="displayName"
            :email="email"
            :email-enabled="!globalConfig.disableUserProfileUpdate"
            @update-user="updateProfile"
          />
        </div>
      </SectionLayout>
    </template>

    <template v-else-if="activeTabKey === 'preferences'">
      <SectionLayout
        :title="$t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.TITLE')"
        :description="
          replaceInstallationName(
            $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.NOTE')
          )
        "
        class="!pt-6"
      >
        <div class="flex flex-col gap-6 items-start">
          <FontSize
            :value="currentFontSize"
            :label="
              $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.TITLE')
            "
            :description="
              $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.NOTE')
            "
            @change="updateFontSize"
          />
          <UserLanguageSelect
            :label="
              $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.TITLE')
            "
            :description="
              $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.NOTE')
            "
          />
        </div>
      </SectionLayout>
      <SectionLayout
        with-border
        :title="$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.TITLE')"
        :description="$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.NOTE')"
      >
        <div
          class="flex flex-col justify-between w-full gap-5 sm:gap-4 sm:flex-row"
        >
          <RadioCard
            v-for="hotKey in hotKeys"
            :id="hotKey.key"
            :key="hotKey.key"
            :label="hotKey.title"
            :description="hotKey.description"
            :is-active="isEditorHotKeyEnabled(hotKey.key)"
            class="sm:flex-1"
            @select="toggleHotKey"
          >
            <img
              :src="hotKey.lightImage"
              :alt="`Light themed image for ${hotKey.title}`"
              class="block object-cover w-full dark:hidden"
            />
            <img
              :src="hotKey.darkImage"
              :alt="`Dark themed image for ${hotKey.title}`"
              class="hidden object-cover w-full dark:block"
            />
          </RadioCard>
        </div>
      </SectionLayout>
    </template>

    <template v-else-if="activeTabKey === 'signature'">
      <SectionLayout
        :title="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.TITLE')"
        :description="
          $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.NOTE')
        "
        class="!pt-6"
      >
        <MessageSignature
          :message-signature="messageSignature"
          :message-signature-position="messageSignaturePosition"
          :user-name="name"
          @update-signature="updateSignature"
          @update-signature-position="updateSignaturePosition"
        />
      </SectionLayout>
    </template>

    <template v-else-if="activeTabKey === 'security'">
      <SectionLayout
        v-if="!globalConfig.disableUserProfileUpdate"
        :title="$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.TITLE')"
        description=""
        class="!pt-6"
      >
        <ChangePassword />
      </SectionLayout>
      <SectionLayout
        v-if="isMfaEnabled"
        with-border
        :title="$t('PROFILE_SETTINGS.FORM.SECURITY_SECTION.TITLE')"
        :description="$t('PROFILE_SETTINGS.FORM.SECURITY_SECTION.NOTE')"
      >
        <MfaSettingsCard />
      </SectionLayout>
      <SectionLayout
        with-border
        :title="$t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.TITLE')"
        :description="$t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.NOTE')"
      >
        <ActiveSessions />
      </SectionLayout>
    </template>

    <template v-else-if="activeTabKey === 'notifications'">
      <Policy :permissions="audioNotificationPermissions">
        <SectionLayout
          :title="$t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.TITLE')"
          :description="
            $t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.NOTE')
          "
          class="!pt-6"
        >
          <AudioNotifications />
        </SectionLayout>
      </Policy>
      <Policy :permissions="notificationPermissions">
        <SectionLayout
          with-border
          :title="$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TITLE')"
          description=""
        >
          <NotificationPreferences />
        </SectionLayout>
      </Policy>
    </template>

    <template v-else-if="activeTabKey === 'api'">
      <SectionLayout
        :title="$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.TITLE')"
        :description="accessTokenDescription"
        class="!pt-6"
      >
        <AccessToken
          :value="currentUser.access_token"
          :disabled="!apiAndWebhooksEnabled"
          @on-copy="onCopyToken"
          @on-reset="resetAccessToken"
        />
      </SectionLayout>
      <SectionLayout
        with-border
        :title="$t('PROFILE_SETTINGS.FORM.API_REFERENCE.TITLE')"
        :description="apiReferenceDescription"
      >
        <NextButton
          :label="$t('PROFILE_SETTINGS.FORM.API_REFERENCE.DOWNLOAD')"
          :is-loading="isDownloadingReference"
          slate
          outline
          type="button"
          icon="i-lucide-download"
          class="rounded-xl"
          @click="downloadPostmanCollection"
        />
      </SectionLayout>
    </template>
  </div>
</template>
